// The read-only entries table the logbook lists share: the trip page, the year
// page, the BIG LOG's entries view, and LogbookTripsTable's expanded trip row.
// One implementation so a fix to a cell or to the continuation row lands
// everywhere.
//
// `entries` are DISPLAY copies (entryWithResolvedEndpoints) -- the caller's
// contract, same as LogbookTrip's displayEntries -- and `onSelectEntry`
// receives that copy: the caller re-looks-up the stored row by id before
// navigating (see useEntryMapLegs' header for why).
//
// `showNotes` off keeps the compact "has notes" icon column. On, the column is
// dropped and each noted entry carries its note in full on a continuation
// <tr> -- TripGroup's leg-row / leg-note-row pattern verbatim, because the
// note has to span every column and the pair has to read and hover as one row
// (App.css owns those rules). A row WITHOUT a continuation must not get
// `leg-row`, or its bottom rule would vanish.
//
// `photosByEntry` adds that entry's photos to the SAME continuation row rather
// than a third <tr>. The pairing CSS is three selectors using the adjacent
// sibling combinator, which is one hop: a third row would need the note row to
// lose its bottom rule too, plus `leg-row:hover + leg-note-row + leg-photo-row`,
// plus the :has() mirror, plus both hovered-in-the-middle cases -- six
// selectors that grow combinatorially with the next continuation row anyone
// adds. One row needs none. `leg-note-row` is therefore the CONTINUATION row's
// class now rather than the note's; App.css's comment already calls it that,
// and renaming it would touch TripGroup.jsx and its suite for nothing.
//
// `tripKey` + `onTripName` and `showDetails` are the big log's two extra
// capabilities, and absorbing them is what let that page stop carrying a
// private row of its own. Only the big log has a Trip Name that leads
// somewhere -- inside one trip, or one year's trips view, the name is the
// row's own label -- and only the big log has a trailing Details arrow.
//
// `showDetails` is a BOOLEAN, not an `onDetails` callback, deliberately: the
// arrow and the row click have the same destination, and a second handler would
// be free to diverge from `onSelectEntry`. That argument was settled once
// already -- EntryPhotoStrip's `+N more` calls onSelectEntry "so there is no
// second navigation rule to keep in step". The shape is `showPhotosToggle`'s.
//
// EVERY optional prop -- photosByEntry/onOpenPhoto, tripKey/onTripName,
// showDetails -- defaults to absent, and each new term below is gated on one.
// That is what leaves LogbookYear, LogbookTripsTable and LogbookTrip rendering
// an identical element tree, and their suites standing unedited is the proof.
//
// The ONE place that is not simply additive is the row's `role="button"`, which
// is dropped whenever the row carries a control of ANY kind -- see
// `withControls` below. It used to be dropped for photos alone, which was the
// rule stated too narrowly: the Details arrow and the Trip Name link sit inside
// the same row and are hidden by the same children-presentational rule. A
// caller passing neither still gets `role="button"` on every row exactly as
// before, so only the big log's markup moves.

import React from 'react';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { displayDate } from '../../fleet-shared/Common.mjs';
import { entryHasNotes, readEntryField } from '../../fleet-shared/LogbookTypes.mjs';
import { placeCell } from '../../fleet-shared/PlaceFields.mjs';
import EntryPhotoStrip from './EntryPhotoStrip';

// One cell, same rules as the logbook list's: { text, title }, with `title`
// set only where a gazetteer name was shortened.
function cellValue(entry, field, placeIndex) {
  const value = readEntryField(entry, field);
  if (field.type === 'date') return { text: displayDate(value) };
  if (value === null || value === undefined) return { text: '' };
  if (field.type === 'place') return placeCell(value, placeIndex);
  return { text: value };
}

export function LogbookEntriesTable({
  entries,
  columns,
  placeIndex,
  showNotes,
  onSelectEntry,
  photosByEntry,
  onOpenPhoto,
  tripKey,
  onTripName,
  showDetails,
}) {
  // The header carries a trailing Notes icon column when the notes themselves
  // are not shown, and a trailing Details column where the host asks for one,
  // so a continuation row has to span one more than `columns` for each. This
  // was `columns.length`, which was correct only while the continuation row
  // existed IFF showNotes -- the two conditions were exact complements. Photos
  // break that: photos on with notes off has a continuation row AND the icon
  // column, and that is the default. Details is the SECOND trailing column that
  // guess missed, and the big log is where both are present at once.
  const continuationSpan = columns.length + (showNotes ? 0 : 1) + (showDetails ? 1 : 0);

  return (
    <Table responsive hover className="align-middle mb-0">
      <thead className="bg-body-tertiary">
        <tr>
          {columns.map((field) => (
            <th key={field.key} scope="col">{field.name}</th>
          ))}
          {!showNotes && <th scope="col" className="text-center">Notes</th>}
          {showDetails && <th scope="col" className="text-end">Details</th>}
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => {
          const note = entryHasNotes(entry) ? entry.notes.trim() : '';
          const withNote = showNotes && note !== '';
          const photos = photosByEntry?.get(entry.id) ?? [];
          const withPhotos = photos.length > 0;
          const withContinuation = withNote || withPhotos;

          // The cells are built once rather than inside the map below, because
          // whether this row has a Trip Name LINK (as opposed to a blank or a
          // plain name) is one of the things `withControls` has to know before
          // the row element is written.
          const cells = columns.map((field) => {
            const { text, title } = cellValue(entry, field, placeIndex);
            return {
              field,
              text,
              title,
              isTrip: Boolean(tripKey) && field.key === tripKey && text !== '' && text != null,
            };
          });

          // Does this row contain a control of its own? An ARIA button's
          // children are children-presentational, so ANY <button> inside a
          // role="button" row is both invalid and unannounced -- the
          // ListGroup.Item trap WeatherLinks documents. The strip is only the
          // commonest case; the Details arrow and the Trip Name link are the
          // other two, and the big log carries all three.
          //
          // Dropping the role costs nothing a reader had: without a tabIndex it
          // was never focusable, so it announced "button" while hiding the very
          // controls that ARE reachable. The row's onClick stays either way --
          // a tap on empty space should still open the entry.
          const withControls = withPhotos || Boolean(showDetails) || cells.some((c) => c.isTrip);
          // The continuation row takes the same answer rather than its own, so
          // a pair the CSS makes read and hover as one row cannot disagree
          // about whether it is a button.
          const rowRole = withControls ? undefined : 'button';
          return (
            // A fragment keyed by the entry: the note and the photos are a
            // second <tr> because they have to span every column.
            <React.Fragment key={entry.id}>
              <tr
                onClick={() => onSelectEntry(entry)}
                role={rowRole}
                style={{ cursor: 'pointer' }}
                className={withContinuation ? 'leg-row' : undefined}
              >
                {cells.map(({ field, text, title, isTrip }) => (
                  <td key={field.key} className="align-middle entered-data" title={title}>
                    {isTrip ? (
                      <Button
                        variant="link"
                        className="p-0 align-baseline text-start"
                        // The ENTRY, not the rendered text: a trip is
                        // identified by an id now, and `text` is only its
                        // label -- two rows reading "Day" are routinely two
                        // different trips.
                        onClick={(e) => { e.stopPropagation(); onTripName(entry, String(text)); }}
                      >
                        {text}
                      </Button>
                    ) : (
                      text
                    )}
                  </td>
                ))}
                {!showNotes && (
                  <td className="align-middle text-center">
                    {note !== '' && (
                      <i
                        className="fa-solid fa-book"
                        title="Has notes"
                        aria-label="Has notes"
                        role="img"
                      ></i>
                    )}
                  </td>
                )}
                {showDetails && (
                  // An arrow rather than the word "More" -- see TripGroupsTable.
                  <td className="align-middle text-end" style={{ width: 56 }}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      aria-label="Details"
                      onClick={(e) => { e.stopPropagation(); onSelectEntry(entry); }}
                    >
                      <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                    </Button>
                  </td>
                )}
              </tr>
              {withContinuation && (
                <tr
                  onClick={() => onSelectEntry(entry)}
                  role={rowRole}
                  style={{ cursor: 'pointer' }}
                  className="leg-note-row"
                >
                  <td
                    colSpan={continuationSpan}
                    className="small text-body-secondary pt-0"
                  >
                    {/* pre-wrap: a note typed as several lines stays that way.
                        On its own div so it doesn't govern the strip. */}
                    {withNote && <div style={{ whiteSpace: 'pre-wrap' }}>{note}</div>}
                    {withPhotos && (
                      <EntryPhotoStrip
                        photos={photos}
                        onOpenPhoto={onOpenPhoto}
                        onOpenEntry={() => onSelectEntry(entry)}
                      />
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
}

export default LogbookEntriesTable;
